import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { createPrpDocument, listPrpDocuments, type PrpContactNoteContent } from '@/api/prp.api';
import { emptyContactNote } from './forms/contact-note-rules';
import { todayISO } from './format';
import { documentOrigin } from './navigation';

/** A contact note started from a calendar visit. */
export interface StartContactNoteInput {
  patientId: string;
  documentDate?: string;
  prefill?: Partial<PrpContactNoteContent>;
  appointmentId?: string;
  groupSessionId?: string;
}

/** Creates the draft (or returns the client's live note for the same visit). */
export async function startContactNote(input: StartContactNoteInput) {
  const { data } = await listPrpDocuments({
    patient_id: input.patientId,
    doc_type: 'irp',
    status: 'signed',
    limit: 1,
  });
  const irp = data[0];
  if (!irp) throw new Error('NO_IRP');
  return createPrpDocument({
    patient_id: input.patientId,
    doc_type: 'contact_note',
    document_date: input.documentDate ?? todayISO(),
    content: { ...emptyContactNote(irp.id), ...input.prefill } as unknown as Record<string, unknown>,
    appointment_id: input.appointmentId,
    group_session_id: input.groupSessionId,
  });
}

/** Start a contact note for a client, linked to their current signed IRP. */
export function useStartContactNote() {
  const navigate = useNavigate();
  const location = useLocation();
  const create = useMutation({
    mutationFn: (input: string | StartContactNoteInput) =>
      startContactNote(typeof input === 'string' ? { patientId: input } : input),
    onSuccess: (doc) =>
      navigate(`/prp/documents/${doc.id}`, {
        state: documentOrigin(location.pathname, location.search),
      }),
    onError: (err) => {
      toast({
        variant: 'destructive',
        title: "Can't start a contact note",
        description:
          err instanceof Error && err.message === 'NO_IRP'
            ? 'This client has no signed IRP yet. Contact notes must address IRP goals.'
            : 'Something went wrong - try again.',
      });
    },
  });
  return create;
}
